import confetti from 'canvas-confetti';
import { useEffect, useRef, useState } from 'react';
import { nextSeat, teamOfSeat, type Seat, type TeamId } from '../../game/match';
import { confirmDialog } from '../../components/Confirm';
import { SpinHub, Wheel, type WheelHandle } from '../../components/Wheel';
import { Avatar, Name, teamAvatarFill } from '../../components/ui';
import type { RoomConnection } from '../../lib/room';
import { unlockAudio } from '../../lib/sound';
import type { RoomView } from '../../shared/room';
import { shareOrCopy } from '../Dashboard';

export const tableUrl = (code: string) => `${location.origin}/r/${code}`;
export const inviteTable = (code: string) => shareOrCopy(`Join our Rang table (${code}) — pick your name and let's play:`, tableUrl(code));

/** "Who are you?" — each phone takes one of the four players, shown by team so partners sit together. */
export function SeatChooser({ view, conn }: { view: RoomView; conn: RoomConnection }) {
  const free = view.seatInfo.filter((s) => !s.claimed).length;
  const take = async (seat: Seat) => {
    unlockAudio();
    const p = view.seats[seat]!;
    const ok = await confirmDialog({
      title: `Are you ${p.name}?`,
      icon: '🙋',
      body: `This phone will play as ${p.name} for ${view.teams[teamOfSeat(seat)].name}. Only you will see ${p.name}'s cards.`,
      confirmLabel: `Yes, I'm ${p.name}`,
      cancelLabel: 'No',
    });
    if (ok) conn.send({ t: 'claim', seat });
  };
  const team = (id: TeamId) => {
    const t = view.teams[id];
    const seats = (id === 'A' ? [0, 2] : [1, 3]) as Seat[];
    return (
      <div key={id} className="overflow-hidden rounded-2xl ring-1 ring-ink/10" style={{ background: `color-mix(in srgb, ${t.color} 7%, white)` }}>
        <p className="flex items-center gap-2 px-3 pt-2.5 pb-1.5 text-xs font-extrabold tracking-wide uppercase" style={{ color: t.color }}>
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color }} aria-hidden />
          {t.name}
          <span className="font-semibold tracking-normal text-ink-soft normal-case">· partners</span>
        </p>
        <ul className="divide-y divide-ink/8 bg-white/60">
          {seats.map((seat) => {
            const p = view.seats[seat]!;
            const info = view.seatInfo[seat]!;
            return (
              <li key={seat}>
                <button
                  type="button"
                  disabled={info.claimed}
                  onClick={() => void take(seat)}
                  aria-label={info.claimed ? `${p.name} has already joined` : `I'm ${p.name}`}
                  className="group flex w-full items-center gap-3 px-3 py-2.5 text-left transition enabled:hover:bg-white disabled:cursor-not-allowed"
                >
                  <span className={info.claimed ? 'opacity-50 grayscale' : ''}>
                    <Avatar name={p.name} avatarKey={p.avatarKey} size={46} ring={t.color} fill={teamAvatarFill(t.color)} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <Name className={`block truncate text-lg leading-tight font-extrabold ${info.claimed ? 'text-ink-soft' : 'text-ink'}`}>{p.name}</Name>
                    <span className="block text-xs font-semibold text-ink-soft">
                      {info.claimed ? (info.online ? 'Already playing on another phone' : 'Joined · away right now') : 'Tap if this is you'}
                    </span>
                  </span>
                  {info.claimed ? (
                    <span className="shrink-0 rounded-full bg-success-600/12 px-2 py-0.5 text-[11px] font-extrabold text-success-700">✓ Joined</span>
                  ) : (
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink text-cream transition group-hover:translate-x-0.5" aria-hidden>
                      →
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };
  return (
    <section className="card p-4" aria-labelledby="who-h">
      <p className="eyebrow text-[10px] text-ink-soft">Joining {view.variant === 'bandh' ? 'Bandh Rang' : 'Open Rang'}</p>
      <h2 id="who-h" className="mt-0.5 font-display text-2xl leading-tight font-extrabold">
        Who are you on this phone?
      </h2>
      <p className="mt-1 text-sm text-ink-soft">
        {free > 0 ? 'Find your own name and tap it. Each person picks their own name on their own phone.' : 'All four players have joined — you can watch the game.'}
      </p>
      <div className="mt-4 grid gap-3">{(['A', 'B'] as const).map(team)}</div>
    </section>
  );
}

/** Who has joined, and the invite link for those who haven't. */
export function LobbySeats({ view }: { view: RoomView }) {
  const active = view.seatInfo.filter((s) => s.claimed && s.online).length;
  const waiting = view.seatInfo.filter((s) => !s.claimed).length;
  return (
    <section className="card overflow-hidden" aria-label="Players">
      <div className="flex items-center justify-between gap-3 px-4 pt-4">
        <div className="min-w-0">
          <h2 className="font-display text-lg leading-tight font-extrabold">{waiting > 0 ? `Waiting for ${waiting} more` : 'Everyone is here!'}</h2>
          <p className="text-xs font-semibold text-ink-soft">
            <span className="text-success-700">{active} of 4 active</span>
            {waiting > 0 && ' · share the link so they can join'}
          </p>
        </div>
        {waiting > 0 && (
          <button type="button" className="btn btn-primary min-h-10 shrink-0 px-3.5 text-sm" onClick={() => void inviteTable(view.code)}>
            🔗 Invite
          </button>
        )}
      </div>
      <div className="mx-4 mt-3 h-1.5 overflow-hidden rounded-full bg-ink/10" role="progressbar" aria-valuemin={0} aria-valuemax={4} aria-valuenow={active} aria-label="Players active">
        <div className="h-full rounded-full bg-success-500 transition-[width] duration-500" style={{ width: `${(active / 4) * 100}%` }} />
      </div>
      <ul className="grid grid-cols-4 gap-2 px-3 pt-4 pb-4">
        {view.seats.map((p, i) => {
          const info = view.seatInfo[i]!;
          const team = view.teams[teamOfSeat(i as Seat)];
          const state = !info.claimed ? 'missing' : info.online ? 'active' : 'away';
          return (
            <li key={i} className="flex min-w-0 flex-col items-center gap-1.5 text-center">
              <span className={`relative rounded-full ${state === 'missing' ? 'border-2 border-dashed border-amber-400 p-[2px]' : ''}`}>
                <span className={state === 'missing' ? 'block opacity-40 grayscale' : 'block'}>
                  <Avatar name={p.name} avatarKey={p.avatarKey} size={state === 'missing' ? 42 : 48} ring={state === 'missing' ? undefined : team.color} fill={teamAvatarFill(team.color)} />
                </span>
                {state === 'active' && (
                  <span className="absolute -right-0.5 -bottom-0.5 h-3.5 w-3.5 rounded-full bg-success-500 ring-2 ring-cream" aria-hidden>
                    <span className="absolute inset-0 animate-ping rounded-full bg-success-500/60" />
                  </span>
                )}
              </span>
              <Name className={`max-w-full truncate text-xs font-bold ${state === 'missing' ? 'text-ink-soft' : 'text-ink'}`}>{p.name}</Name>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold tracking-wide whitespace-nowrap ${
                  state === 'active' ? 'bg-success-600/12 text-success-700' : state === 'away' ? 'bg-ink/8 text-ink-soft' : 'bg-amber-100 text-amber-800 ring-1 ring-amber-300'
                }`}
              >
                {state === 'active' ? (i === view.you ? '● You' : '● Active') : state === 'away' ? 'Away' : 'Not joined'}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The wheel has stopped: a celebratory popup naming the first dealer (and who picks Rang). */
function FirstDealerPopup({ view, seat, onDone }: { view: RoomView; seat: Seat; onDone: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const dealer = view.seats[seat]!;
  const team = view.teams[teamOfSeat(seat)];
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    // A popup sits above the page, so the confetti has to be drawn inside it.
    if (canvas.current) {
      const burst = confetti.create(canvas.current, { resize: true });
      void burst({ particleCount: 110, spread: 80, origin: { y: 0.3 }, colors: [team.color, '#d4a84b', '#f7f1e3'], disableForReducedMotion: true });
    }
    return () => d?.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // In both games the next player round the table picks Rang (in Bandh Rang, in secret).
  const callerSeat = nextSeat(seat, view.rules.turnDirection);
  const caller = view.seats[callerSeat]!;
  return (
    <dialog
      ref={ref}
      aria-labelledby="first-dealer-h"
      onCancel={(e) => {
        e.preventDefault();
        onDone();
      }}
      className="m-auto w-[min(22rem,calc(100%-2.5rem))] max-w-none rounded-[2rem] bg-transparent p-0 backdrop:bg-black/60 backdrop:backdrop-blur-[2px]"
    >
      <canvas ref={canvas} className="pointer-events-none fixed inset-0 z-10 h-full w-full" aria-hidden />
      <div
        className="animate-pop relative overflow-hidden rounded-[2rem] p-[1.5px] shadow-[0_30px_70px_-20px_rgb(0_0_0/0.85)]"
        style={{ background: 'linear-gradient(160deg, rgb(236 212 143 / 0.9), rgb(236 212 143 / 0.15) 45%, rgb(236 212 143 / 0.7))' }}
      >
        <div className="rounded-[calc(2rem-1.5px)] bg-cream px-6 pt-7 pb-5 text-center text-ink">
          <p className="eyebrow text-[10px] text-ink-soft">The wheel has spoken</p>
          <div className="relative mx-auto mt-4 w-fit">
            <span className="pointer-events-none absolute -inset-4 rounded-full bg-gold/25 blur-xl" aria-hidden />
            <span className="relative block">
              <Avatar name={dealer.name} avatarKey={dealer.avatarKey} size={96} ring={team.color} fill={teamAvatarFill(team.color)} />
            </span>
            <span className="absolute -right-2 -bottom-1 rounded-full bg-gold px-2 py-0.5 text-[10px] font-extrabold tracking-wider text-ink shadow ring-2 ring-cream">
              DEALER
            </span>
          </div>
          <h2 id="first-dealer-h" className="mt-4 font-display text-3xl leading-tight font-extrabold">
            <Name>{dealer.name}</Name> deals first!
          </h2>
          <p className="mt-1 text-sm text-ink-soft">
            <span className="font-bold" style={{ color: team.color }}>
              {team.name}
            </span>{' '}
            {view.variant === 'bandh' ? (
              <>
                are dealing · <Name className="font-semibold text-ink">{caller.name}</Name> picks a hidden Rang and{' '}
                <span className="font-bold" style={{ color: view.teams[teamOfSeat(callerSeat)].color }}>
                  {view.teams[teamOfSeat(callerSeat)].name}
                </span>{' '}
                try to run.
              </>
            ) : (
              <>
                are dealing · <Name className="font-semibold text-ink">{caller.name}</Name> picks Rang from the first 5 cards.
              </>
            )}
          </p>
          <button type="button" className="btn btn-ink mt-5 h-12 w-full text-base" onClick={onDone} autoFocus>
            To the table →
          </button>
        </div>
      </div>
    </dialog>
  );
}

/**
 * The shared magic wheel. The table picks the dealer; every phone spins to the
 * same result at the same moment. `reveal` plays the spin for a result that just arrived.
 */
export function SpinStage({ view, conn, reveal, onDone }: { view: RoomView; conn: RoomConnection; reveal: boolean; onDone: () => void }) {
  const wheel = useRef<WheelHandle>(null);
  const [spinning, setSpinning] = useState(false);
  const [landed, setLanded] = useState<Seat | null>(null);
  const allSeated = view.seatInfo.every((s) => s.claimed);
  const canSpin = view.you !== null && allSeated && view.firstDealer === null;

  useEffect(() => {
    if (!reveal || view.firstDealer === null) return;
    const seat = view.firstDealer;
    let cancelled = false;
    setSpinning(true);
    void wheel.current?.spinTo(seat).then(() => {
      if (cancelled) return;
      setSpinning(false);
      setLanded(seat);
    });
    return () => {
      cancelled = true;
    };
    // Spin once per result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reveal, view.spinId]);

  const winner = landed !== null ? view.seats[landed]! : null;
  return (
    <section aria-label="Who deals first">
      <h2 className="mb-3 text-center font-display text-2xl font-extrabold">Who deals first?</h2>
      <div
        className="relative rounded-full"
        role={canSpin ? 'button' : 'img'}
        tabIndex={canSpin ? 0 : -1}
        aria-label={canSpin ? 'Spin the dealer wheel' : 'Dealer wheel'}
        onClick={() => {
          if (!canSpin || spinning) return;
          unlockAudio();
          conn.send({ t: 'spin' });
        }}
      >
        <Wheel
          ref={wheel}
          seats={view.seats}
          teams={view.teams}
          center={
            canSpin && !spinning && landed === null ? (
              <SpinHub
                onSpin={() => {
                  unlockAudio();
                  conn.send({ t: 'spin' });
                }}
              />
            ) : undefined
          }
        />
      </div>
      <div className="mt-5 min-h-28" aria-live="assertive">
        {spinning ? (
          <p className="text-center font-display text-2xl font-bold text-gold-soft">Spinning…</p>
        ) : winner ? (
          <FirstDealerPopup view={view} seat={landed!} onDone={onDone} />
        ) : canSpin ? (
          <p className="text-center text-sm font-semibold text-cream/75">Tap SPIN in the middle of the wheel.</p>
        ) : (
          <p className="panel p-4 text-center text-cream/80">{allSeated ? 'Waiting for someone to spin…' : 'The wheel unlocks when all four players have joined.'}</p>
        )}
      </div>
    </section>
  );
}
